import { PatientPharmacyEditDrawer } from '@app/icare/entities/patient/edits/PatientPharmacyEditDrawer'
import {
  patientFindingsQueryOptions,
  selectByGroupAndGroupByRelationId,
} from '@app/icare/entities/patient/queries/patientFindingsQueryOptions'
import { PatientId } from '@app/icare/entities/patient/schema/PatientId'
import type { PatientPharmacyRelation } from '@app/icare/entities/patient/schema/PatientPharmacy'
import { PharmacyConfig } from '@app/icare/entities/pharmacy/config'
import { patientPharmacyQueries } from '@app/icare/entities/pharmacy/queries/patientPharmacyQueries'
import { RelationOverview } from '@app/icare/routes/_app/patienten/$id/_edit/-components/RelationOverview'
import { useRelationCommitNavigation } from '@app/icare/routes/_app/patienten/$id/_edit/-components/useRelationCommitNavigation'
import { isActivePatientRelation } from '@app/icare/shared/api'
import { entityParams } from '@pkg/db/utils'
import { useQuery, useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'

export const Route = createFileRoute('/_app/patienten/$id/_edit/_pharmacy/apotheken/')({
  params: entityParams({ id: PatientId }),
  loader: ({ params, context: { queryClient } }) =>
    queryClient.ensureQueryData(patientPharmacyQueries.forIndex(params.id)),
  component: RouteComponent,
})

function RouteComponent() {
  const { id } = Route.useParams()
  const navigate = Route.useNavigate()
  const { data: pharmacies = [] } = useSuspenseQuery(patientPharmacyQueries.forIndex(id))
  const { data: findings = {} } = useQuery({
    ...patientFindingsQueryOptions(id),
    select: selectByGroupAndGroupByRelationId('pharmacies'),
  })

  const handleAfterCommit = useRelationCommitNavigation<PatientPharmacyRelation>({
    isInactive: relation => !isActivePatientRelation(relation),
    onCreateNavigate: relation =>
      navigate({
        to: '/patienten/$id/apotheken/$relation',
        params: { id, relation: relation.id },
      }),
    onInactiveEditNavigate: () => navigate({ to: '.', replace: true }),
  })

  return (
    <RelationOverview
      items={pharmacies}
      labels={PharmacyConfig}
      findings={findings}
      basePath={`/patienten/${id}/apotheken`}
      onEdit={params =>
        void PatientPharmacyEditDrawer.show({
          patientId: id,
          onAfterCommit: handleAfterCommit,
          ...params,
        })
      }
    />
  )
}
